import { shopSchema } from "./schemas/shop";
import { getCurrentLanguage } from "@/hooks/use-language";
import { getToken } from "@/hooks/use-token";
import { z } from "zod";
import { paymentSchema } from "@/features/payment/utils";
import { getShopFromLocalStorage } from "@/hooks/use-shop";
import { machineSchema } from "@/hooks/use-machine";
import {
  applyPaymentResponseSchema,
  collectorsDiscountSchema,
} from "@/hooks/use-apply-payment";
import { errorSchema, parseResponse } from "./api";
import { UserSchema } from "@/hooks/use-user";
import { resSchema } from "@/hooks/use-active-reservations";
import { cashPaymentStatusSchema } from "@/features/payment/cash-payment-status";
import { priceSchema } from "@/features/payment/balance-payment-confirm";
import { useQuery } from "@tanstack/react-query";
import { getTrackedSessionId } from "./kiosk-session-tracking";
import { getCashAgentUrl, getServerUrl } from "./runtime-config";

const startHopperTopupResponseSchema = z.object({ paymentKey: z.string() });

export { getServerUrl };

function getBackendUrls() {
  return [getCashAgentUrl(), getServerUrl()];
}

export function getBaseUrl() {
  return getBackendUrls()[0];
}

export function useIsCashAgentAvailable() {
  return useQuery({
    queryKey: ["is-cash-agent-available"],
    refetchInterval: 10000,
    queryFn: async () => {
      const response = await fetch(`${getCashAgentUrl()}/hello`)
        .then((res) => res.text())
        .catch(() => null);

      return response === "Hello World";
    },
  });
}

export const openingHoursSchema = z.object({
  opening: z.string(),
  closing: z.string(),
  regionCode: z.string(),
  temporaryClosed: z
    .object({
      until: z.string(),
      message: z.object({
        hu: z.string(),
        en: z.string(),
        de: z.string(),
        sk: z.string(),
      }),
      showOnKiosk: z.boolean().optional(),
      blockOperations: z.boolean().optional(),
    })
    .nullable()
    .optional(),
});

export type OpeningHours = z.infer<typeof openingHoursSchema>;

export const orderStatusSchema = z.object({
  status: z.enum([
    "waiting-for-payment",
    "fulfilled",
    "failed",
    "payment-rejected",
  ]),
  sessionId: z.string().nullable(),
  machine: z.object({
    id: z.coerce.number(),
    type: z.enum(["mosogep", "szaritogep", "ozon"]),
    storePlace: z.coerce.number(),
    capacity: z.coerce.number(),
  }),
  spinningState: z.enum(["spinning", "idle", "unreachable"]),
  startSignalRemainingSeconds: z.number().nullable(),
  collectorsDiscount: collectorsDiscountSchema.optional().catch(undefined),
});

export type OrderStatus = z.infer<typeof orderStatusSchema>;

export const api = {
  openingHours: async (shopId: number) => {
    const res = await myFetch(`/shops/kiosk/${shopId}/opening-hours`);
    return openingHoursSchema.parse(await res.json());
  },
  auth: {
    kiosk: (kioskKey: string) =>
      myFetch(`/auth/kiosk/${kioskKey}`)
        .then((res) => res.json())
        .then((shop) => shopSchema.parse(shop)),
  },
  machine: async (machineId: number) => {
    const token = getToken();
    return await myFetch(
      token ? `/machines/${machineId}` : `/machines/kiosk/${machineId}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: ("Bearer " + token) as string,
        },
      },
    )
      .then((res) => res.json())
      .then((machine) => machineSchema.parse(machine));
  },
  createPayment: async ({
    machineId,
    dryerBundleId,
    couponKey,
  }: {
    machineId: number;
    dryerBundleId?: number;
    couponKey?: string;
  }) => {
    const token = getToken();
    const shop = getShopFromLocalStorage();
    return await myFetch(
      token ? `/payment/create-payment` : `/payment/kiosk/create-payment`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Kiosk-Key": shop?.kioskKey ?? "",
          ...(token ? { authorization: "Bearer " + token } : {}),
        },
        body: JSON.stringify({ machineId, dryerBundleId, couponKey }),
      },
    )
      .then((data) => data.json())
      .then((payment) => paymentSchema.parse(payment));
  },
  applyPayment: async ({
    purchaseType,
    kioskPaymentNonceKey,
  }: {
    purchaseType:
      | "KIOSK_CASH"
      | "KIOSK_BANK_CARD"
      | "FULL_DISCOUNT"
      | "BALANCE";
    kioskPaymentNonceKey: string;
  }) => {
    const token = getToken();
    const shop = getShopFromLocalStorage();
    const baseUrl = token
      ? `/payment/apply-payment`
      : `/payment/kiosk/apply-payment`;

    const res = await myFetch(baseUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token
          ? { Authorization: "Bearer " + token }
          : { "Kiosk-Key": shop?.kioskKey ?? "" }),
      },
      body: JSON.stringify({
        kioskPaymentNonceKey: kioskPaymentNonceKey,
        lang: shop?.locale ?? "hu",
        purchaseType: purchaseType,
      }),
    });

    return await parseResponse(res, applyPaymentResponseSchema);
  },
  /**
   * Wash start guide. Asks the cash-agent first; an order paid online may not
   * have synced there yet (404), so the backend gets asked too.
   */
  orderStatus: async (orderId: string) => {
    const path = `/orders/${encodeURIComponent(orderId)}/status`;
    const fromCashAgent = await fetch(`${getCashAgentUrl()}${path}`).catch(
      () => null,
    );
    const res = fromCashAgent?.ok
      ? fromCashAgent
      : await fetch(`${getServerUrl()}${path}`);
    if (!res.ok) {
      throw new Error(`Order status failed with status ${res.status}`);
    }
    return orderStatusSchema.parse(await res.json());
  },
  abortCashPayment: async (shopId: number, paymentKey?: string) => {
    return await myFetch(`/payment/kiosk/abort-cash-payment/${shopId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(paymentKey ? { paymentKey } : {}),
    });
  },
  finishHopperTopUp: async (shopId: number, paymentKey?: string) => {
    const token = getToken();
    const shop = getShopFromLocalStorage();
    const res = await myFetch(`/payment/finish-hopper-top-up/${shopId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "Kiosk-Key": shop?.kioskKey ?? "",
      },
      body: JSON.stringify(paymentKey ? { paymentKey } : {}),
    });
    return await parseResponse(res, undefined);
  },
  whoAmI: async () => {
    const shop = getShopFromLocalStorage();
    console.log("asdfasdfasdf", shop?.currencyCode);
    const token = getToken();
    const response = await myFetch(`/auth/whoami/${shop?.currencyCode}`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }).then((res) => res.json());

    return UserSchema.parse(response);
  },
  reservations: {
    activesForUser: async () => {
      const token = getToken();
      const shop = getShopFromLocalStorage();
      const res = await myFetch(
        `/reservations/actives-for-user/${shop?.shopId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: ("Bearer " + token) as string,
            "Kiosk-Key": shop?.kioskKey ?? "",
          },
        },
      ).then(async (res) => {
        if (!res.ok) {
          throw new Error(await res.text());
        }
        return await res.json();
      });

      return resSchema.array().parse(res);
    },
  },
  paymentStatus: async (paymentKey: string) => {
    const res = await myFetch(
      `/payment/kiosk/cash-payment-status/${paymentKey}`,
    );
    return await parseResponse(res, cashPaymentStatusSchema);
  },
  login: async (username: string, password: string, currency: string) => {
    const shop = getShopFromLocalStorage();
    const response = await myFetch(`/auth/kiosk/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Kioks-Key": shop!.kioskKey,
        "Kiosk-Key": shop!.kioskKey,
        "country-code": shop!.currencyCode === "EUR" ? "sk" : "hu",
        "lang-key": getCurrentLanguage(),
      },
      body: JSON.stringify({ username, password, currency }),
    }).catch(() => ({
      ok: false,
      json: () => Promise.resolve({}),
    }));

    if (response.ok) {
      const data = z
        .object({ auth_token: z.string(), image: z.string().nullish() })
        .parse(await response.json());
      return { type: "success" as "success", data };
    }

    const error = await response
      .json()
      .then(async (res) => {
        return errorSchema.parse(res).message;
      })
      .catch(() => "server-message-internal-server-error" as const);
    return { type: "error" as "error", error };
  },
  checkBalancePayment: async ({
    kioskPaymentNonceKey,
  }: {
    kioskPaymentNonceKey: string;
  }) => {
    const token = getToken();
    const shop = getShopFromLocalStorage();
    return await myFetch(`/payment/check-balance-payment`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        authorization: "Bearer " + token,
        "Kiosk-Key": shop!.kioskKey,
      },
      body: JSON.stringify({
        kioskPaymentNonceKey: kioskPaymentNonceKey,
      }),
    }).then((res) => parseResponse(res, priceSchema));
  },

  applyCoupon: async ({
    couponKey,
    machineId,
    dryerBundleId,
  }: {
    couponKey: string;
    machineId?: number;
    dryerBundleId?: number;
  }) => {
    const token = getToken();
    const shop = getShopFromLocalStorage();
    return await myFetch(token ? `/coupon/apply` : "/coupon/kiosk/apply", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Kiosk-Key": shop!.kioskKey,
        ...(token ? { authorization: "Bearer " + token } : {}),
      },
      body: JSON.stringify({
        couponCode: couponKey,
        machineId,
        dryerBundleId,
      }),
    }).then((res) =>
      parseResponse(
        res,
        z.object({
          couponKey: z.string().nullish(),
          fixDiscount: z.coerce.number(),
          percentage: z.coerce.number(),
          machines: z
            .array(z.string())
            .transform((val) =>
              val.length === 0 ? ["mosogep", "szaritogep"] : val,
            ),
          capacities: z.array(z.string()),
        }),
      ),
    );
  },

  startHopperTopup: async () => {
    const shop = getShopFromLocalStorage();
    const token = getToken();
    const res = await myFetch(`/payment/start-hopper-top-up/${shop!.shopId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: ("Bearer " + token) as string,
        "Kiosk-Key": shop?.kioskKey ?? "",
        "lang-key": getCurrentLanguage(),
      },
    });
    return await parseResponse(res, startHopperTopupResponseSchema);
  },

  getContractsToAccept: async ({
    username,
    password,
  }: {
    username: string;
    password: string;
  }) => {
    const language = getCurrentLanguage();
    const shop = getShopFromLocalStorage();
    if (!shop) {
      return {
        type: "error" as "error",
        error:
          "server-message-internal-server-error" as "server-message-internal-server-error",
      };
    }

    const response = await myFetch(
      `/contracts/v2/contracts-to-accept/${username}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          lang: language,
          "kiosk-key": shop.kioskKey,
          "country-code": shop.currencyCode === "EUR" ? "sk" : "hu",
        },
        body: JSON.stringify({ password }),
      },
    );

    return parseResponse(response, z.any());
  },

  acceptContracts: async ({
    username,
    password,
    contracts,
  }: {
    username: string;
    password: string;
    contracts: { docTypeId: number; version: number; accepted: boolean }[];
  }) => {
    const language = getCurrentLanguage();
    const shop = getShopFromLocalStorage();
    if (!shop) {
      return {
        type: "error" as "error",
        error:
          "server-message-internal-server-error" as "server-message-internal-server-error",
      };
    }

    const response = await myFetch(`/contracts/v2/accept/${username}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        lang: language,
        "country-code": shop.currencyCode === "EUR" ? "sk" : "hu",
      },
      body: JSON.stringify({ password, contracts }),
    });

    return parseResponse(response, undefined);
  },

  getGuestContracts: async () => {
    const shop = getShopFromLocalStorage();
    const response = await myFetch(`/contracts/v2/registration`, {
      headers: {
        "country-code": shop?.currencyCode === "EUR" ? "sk" : "hu",
      },
    });
    return parseResponse(
      response,
      z.any().transform((elem) => elem.documents),
    );
  },

  generateKioskInvoice: async (data: {
    paymentId: string;
    lang: string;
    email: string;
    billingType: "CORPORATE" | "PERSONAL" | "SOLE_PROPRIETOR";
    username: string;
    city: string;
    zip: string;
    street: string;
    taxnum: string;
    groupTaxNum?: string | null;
    registrationNumber?: string | null;
    preferredTaxnum?: "group" | "regular" | null;
  }) => {
    const response = await myFetch(`/finance-events/kiosk/generate-invoice`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(await response.text());
    }

    return z
      .object({ queued: z.boolean() })
      .catch({ queued: false })
      .parse(await response.json().catch(() => ({ queued: false })));
  },
};

const myFetch = async (...params: Parameters<typeof fetch>) => {
  const urls = getBackendUrls();
  for (let i = 0; i < urls.length; i++) {
    try {
      const options = params[1] ?? {};
      const trackedSessionId = getTrackedSessionId();
      const response = await fetch(`${urls[i]}${params[0]}`, {
        ...options,
        headers: {
          "lang-key": getCurrentLanguage(),
          ...(trackedSessionId
            ? { "tracked-session-id": trackedSessionId }
            : {}),
          ...options.headers,
        },
      });
      // A hosted cash-agent behind a proxy returns a gateway error while its
      // pod starts. Preserve read fallback without replaying payment mutations.
      if (
        i < urls.length - 1 &&
        ["GET", "HEAD"].includes((options.method ?? "GET").toUpperCase()) &&
        [502, 503, 504].includes(response.status)
      ) {
        continue;
      }
      return response;
    } catch (err) {
      if (i === urls.length - 1) {
        throw err;
      }
    }
  }

  throw new Error("No backend urls");
};
